import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { productionApi } from "./productionApi.ts";

type Answers = Record<string, unknown>;
type ReflectionRecord = { key: string; moduleId: string; answers: Answers; updatedAt?: string };
type SaveState = "idle" | "saving" | "saved" | "error";
type ReflectionContextValue = {
  records: Record<string, ReflectionRecord>;
  saveStates: Record<string, SaveState>;
  ready: boolean;
  error: string;
  update: (key: string, moduleId: string, answers: Answers) => void;
  flush: (key: string) => Promise<void>;
  reload: () => Promise<void>;
};

const ReflectionContext = createContext<ReflectionContextValue | null>(null);

export function ReflectionProvider({ children }: { children: ReactNode }) {
  const [records, setRecords] = useState<Record<string, ReflectionRecord>>({});
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const latest = useRef(records);
  latest.current = records;

  const reload = useCallback(async () => {
    setError(""); setReady(false);
    try {
      const result = await productionApi<{ reflections: ReflectionRecord[] }>("/api/reflections");
      const byKey = Object.fromEntries(result.reflections.map((item) => [item.key, item]));
      latest.current = byKey; setRecords(byKey); setReady(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudieron cargar las reflexiones guardadas.");
      setReady(true);
    }
  }, []);

  useEffect(() => { void reload(); return () => { Object.values(timers.current).forEach(clearTimeout); }; }, [reload]);

  const flush = useCallback(async (key: string) => {
    const timer = timers.current[key];
    if (timer) { clearTimeout(timer); delete timers.current[key]; }
    const record = latest.current[key];
    if (!record) return;
    setSaveStates((previous) => ({ ...previous, [key]: "saving" }));
    try {
      const result = await productionApi<{ updatedAt?: string }>(`/api/reflections/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ moduleId: record.moduleId, answers: record.answers }) });
      setRecords((previous) => ({ ...previous, [key]: { ...previous[key], updatedAt: result.updatedAt } }));
      setSaveStates((previous) => ({ ...previous, [key]: "saved" }));
    } catch {
      setSaveStates((previous) => ({ ...previous, [key]: "error" }));
    }
  }, []);

  const update = useCallback((key: string, moduleId: string, answers: Answers) => {
    const record = { key, moduleId, answers };
    latest.current = { ...latest.current, [key]: record };
    setRecords(latest.current);
    setSaveStates((previous) => ({ ...previous, [key]: "saving" }));
    if (timers.current[key]) clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => { void flush(key); }, 800);
  }, [flush]);

  const value = useMemo(() => ({ records, saveStates, ready, error, update, flush, reload }), [records, saveStates, ready, error, update, flush, reload]);
  if (!ready) return <main className="empty-state"><p className="eyebrow">Familias que Acompañan</p><h1>Cargando tus respuestas guardadas…</h1></main>;
  if (error) return <main className="empty-state"><p className="eyebrow">Familias que Acompañan</p><h1>No hemos podido cargar tus respuestas</h1><p>{error}</p><button className="primary" onClick={() => void reload()}>Volver a intentarlo</button></main>;
  return <ReflectionContext.Provider value={value}>{children}</ReflectionContext.Provider>;
}

export function useSavedReflection<T extends Answers>(key: string, moduleId: string, initial: T) {
  const context = useContext(ReflectionContext);
  if (!context) throw new Error("useSavedReflection debe usarse dentro de ReflectionProvider.");
  const answers = (context.records[key]?.answers ?? initial) as T;
  const setAnswers = useCallback((next: T) => context.update(key, moduleId, next), [context, key, moduleId]);
  return { answers, setAnswers, saveState: context.saveStates[key] ?? "idle", flush: () => context.flush(key) };
}

export function reflectionSaveMessage(state: SaveState): string {
  if (state === "saving") return "Guardando tu respuesta…";
  if (state === "saved") return "Respuesta guardada en tu recorrido.";
  if (state === "error") return "No se ha podido guardar. Comprueba tu conexión y vuelve a escribir la respuesta.";
  return "";
}

